import React, { useEffect, useState } from 'react';
import { Flex, Space, Typography } from 'antd';
import ReviewForm from './Review';
import { MOCK_SUMMARY } from './constants';
import { toastError, toastSuccess } from '../Toast/ToastComponent';
import EmployeesReviewApi from '../../Api/EmployeesReviewApi';
import SkillMatrixApi from '../../Api/SkillMatrixApi';
import { useAuth } from '../../Context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa6';

const { Title } = Typography;

const feedbackFields = [
  {
    label: 'Manager Feedback',
    placeholder:
      "Provide detailed feedback on the employee's performance, accomplishments, and contributions...",
  },
  {
    label: 'Areas of Improvement',
    placeholder:
      'Identify specific areas where the ennployee can improve and grow...',
  },
];

/**
 * Transform form values to API payload format
 * @param {object} formValues - { employee_review: { rates: [...], feedback: [...] } }
 * @param {object} params - { month, year, userId, status }
 */
function buildApiPayload(
  formValues,
  { review_id, status, skillRating, user_id, user_role_id },
) {
  const rating = formValues?.manager_review?.rating || [];
  const feedback = formValues?.manager_review?.feedback || [];
  const over_all_rating = formValues?.manager_review?.over_all_rating || 0;

  const ratingArray = skillRating.map((row, index) => ({
    id: row.id,
    value: rating[index]?.value ?? 0,
  }));

  const feedbackArray = feedbackFields.map((field, index) => ({
    question: field.label,
    answer: feedback[index]?.answer ?? '',
  }));

  const payload = {
    review_id,
    user_id,
    user_role_id,
    manager_review: {
      rating: ratingArray,
      feedback: feedbackArray,
      over_all_rating,
    },
  };

  if (status) {
    payload.status = status;
  }

  return payload;
}

const ManagerReviewComp = ({ reviewData }) => {
  const navigate = useNavigate();

  const [saving, setSaving] = useState(false);
  const [skillRating, setSkillRating] = useState(null);
  const { authState } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      const skillRating = await fetchSkillRating();
      if (skillRating) {
        setSkillRating(skillRating);
      }
    };
    fetchData();
    return () => {
      setSkillRating(null);
    };
  }, []);

  async function fetchSkillRating() {
    try {
      const response = await SkillMatrixApi.getList({});
      return response?.data?.result || null;
    } catch (error) {
      console.error('Error fetching skill rating:', error);
      toastError(
        error?.response?.data?.message || 'Failed to fetch skill rating.',
      );
    }
  }
  const handleSubmit = async (values) => {
    try {
      setSaving(true);
      const payload = buildApiPayload(values, {
        skillRating,
        status: 'Hr Review',
        review_id: reviewData?.id,
        user_id: authState?.id,
        user_role_id: authState?.userRoleID,
      });
      await EmployeesReviewApi.create(payload);
      toastSuccess('Review submitted successfully.');
      navigate('/employees_review');
    } catch (error) {
      console.error('Error submitting review:', error);
      toastError(error?.response?.data?.message || 'Failed to submit review.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = async (values) => {
    try {
      setSaving(true);
      const payload = buildApiPayload(values, {
        status: 'Draft',
        skillRating,
        review_id: reviewData?.id,
        user_id: authState?.id,
        user_role_id: authState?.userRoleID,
      });
      await EmployeesReviewApi.create(payload);
      toastSuccess('Draft saved successfully.');
      navigate('/employees_review');
    } catch (error) {
      console.error('Error saving draft:', error);
      toastError(error?.response?.data?.message || 'Failed to save draft.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-fluid px-2 px-md-3">
      <Flex align="center" gap={8} className="mb-3">
        <span
          className={`border px-2 pb-1 cursor-pointer`}
          onClick={() => navigate(-1)}
        >
          <FaArrowLeft size={16} />
        </span>
        <Title level={4} className="text-dark fw-semibold mt-0 mb-1">
          Manager Review
        </Title>
      </Flex>
      <ReviewForm
        monthLabel={'Manager Review'}
        readOnly={saving}
        onSubmit={handleSubmit}
        onSaveDraft={handleSaveDraft}
        value={reviewData}
      >
        <Space direction="vertical" size="middle" className="w-100">
          <ReviewForm.EmployeeInfoHeader
            employeeName={
              reviewData?.NewUserMaster
                ? `${reviewData?.NewUserMaster?.first_name} ${reviewData?.NewUserMaster?.last_name}`
                : '--'
            }
            department={reviewData?.department_name || '--'}
            manager={reviewData?.department_head_name || '--'}
            reviewMonth={`${reviewData?.month} ${reviewData?.year}`}
          />

          <ReviewForm.EmployeeReviewSummary
            headerTitle="Employee Review Summary"
            summary={{
              totalHours: reviewData?.total_hours || 0,
              projects: reviewData?.projects_worked || 0,
              leaves: reviewData?.total_leaves || 0,
              attendancePct: reviewData?.attendance_percentage || 0,
            }}
            feedback={reviewData?.employee_review?.feedback || []}
            rates={reviewData?.employee_review?.rating || []}
          />
          <ReviewForm.PerformanceRating
            title="Manager Evaluation"
            subtitle="Provide your assessment of the employee's performance"
            rows={reviewData?.manager_review_populated || skillRating}
            parentKey={['manager_review', 'rating']}
          />
          <ReviewForm.Feedback
            title="Manager Feedback"
            subtitle="Share achievements, challenges, and improvements for this review period."
            fields={feedbackFields}
            parentKey={['manager_review', 'feedback']}
          />

          <ReviewForm.OverallRating
            title="Overall Rating"
            subtitle="Overall rating of the employee's performance"
            name={['manager_review', 'over_all_rating']}
          />

          <ReviewForm.Actions />
        </Space>
      </ReviewForm>
    </div>
  );
};

export default ManagerReviewComp;
